// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text } from '@/components/Themed';
import TrophyImage from '@/assets/images/trophy.png';
import { useUser } from './user-context';
import { getSharedBffWebSocket, TourCreatedEvent } from '@/services/bffWebSocket';
import { setCurrentTourId } from '@/services/currentTour';
import { useI18n } from '@/i18n';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';

type TourSummary = {
  id: string;
  tourName?: string;
  status?: string;
  playerNames?: string[];
  createdAt?: string;
  imageKey?: string | null;
  imageThumbKey?: string | null;
};

export default function TournamentsScreen() {
  const { debug: debugParam } = useLocalSearchParams<{ debug?: string }>();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { user, clear } = useUser();
  const { t } = useI18n();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const [tours, setTours] = useState<TourSummary[]>([]);
  const [statusNote, setStatusNote] = useState<string>(t('tournaments.loading'));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [tourStarting, setTourStarting] = useState(false);
  const [newTourName, setNewTourName] = useState(() => t('tournaments.defaultName'));
  const [openTourMenuId, setOpenTourMenuId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const pendingRequestIdRef = useRef<string | null>(null);
  const pendingStatusRequestIdRef = useRef<string | null>(null);
  const pendingTourPayloadRef = useRef<Record<string, unknown> | null>(null);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'tournaments',
      }),
    [apiBaseUrl, user?.token]
  );

  const visibleTours = useMemo(() => {
    if (showArchived) return tours;
    return tours.filter((tour) => (tour.status ?? '').toLowerCase() !== 'archived');
  }, [showArchived, tours]);

  const fetchTours = useCallback(async () => {
    if (!user?.token) {
      clear();
      router.replace('/login');
      return;
    }
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      console.log('EXPO_PUBLIC_API_BASE_URL', process.env.EXPO_PUBLIC_API_BASE_URL);
      const playerResp = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
        headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (playerResp.status === 401 || playerResp.status === 403) {
        clear();
        router.replace('/login');
        return;
      }
      if (playerResp.status === 404) {
        clear();
        router.replace('/login?reason=user-not-found');
        return;
      }
      if (!playerResp.ok) throw new Error(await playerResp.text());
      const player = await playerResp.json() as { id: string };
      const response = await fetch(`${apiBaseUrl}/api/tours?playerId=${player.id}`, {
        headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (!response.ok) throw new Error(await response.text());
      const data = (await response.json()) as Array<{
        id: string;
        tourName?: string;
        TourName?: string;
        status?: string;
        Status?: string;
        playerNames?: string[];
        PlayerNames?: string[];
        createdAt?: string;
        CreatedAt?: string;
        imageKey?: string | null;
        ImageKey?: string | null;
        imageThumbKey?: string | null;
        ImageThumbKey?: string | null;
      }>;
      setTours(data.map((t) => ({
        id: t.id,
        tourName: t.tourName ?? t.TourName,
        status: t.status ?? t.Status,
        playerNames: t.playerNames ?? t.PlayerNames,
        createdAt: t.createdAt ?? t.CreatedAt,
        imageKey: t.imageKey ?? t.ImageKey ?? null,
        imageThumbKey: t.imageThumbKey ?? t.ImageThumbKey ?? null,
      })));
      setStatusNote(data.length ? '' : t('tournaments.empty'));
      if (data.length === 0) {
        setCurrentTourId(null);
      }
    } catch (err) {
      setError(t('tournaments.loadError'));
      setTours([]);
      setStatusNote('');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, user?.id, user?.token, clear, router, t]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (isFocused) {
      socket.setSubscriptionContext({ roundId: null, tourId: null });
    }
    const unsubscribe = socket.onMessage((message) => {
      if ((message as { type?: string }).type === 'tour.update.ack') {
        pendingStatusRequestIdRef.current = null;
        setStatusNote(t('tournaments.statusUpdateAck'));
        return;
      }
      const payload = message as TourCreatedEvent;
      if (payload.type === 'tour.created') {
        const incomingRequestId = payload.requestId ?? payload.RequestId;
        const isMatch = pendingRequestIdRef.current && incomingRequestId === pendingRequestIdRef.current;
        if (!isMatch)
        {
          // Only auto-navigate when we initiated the tour; otherwise just refresh list.
          fetchTours();
          setStatusNote(t('tournaments.createdNotice'));
          return;
        }
        pendingRequestIdRef.current = null;
        setTourStarting(false);
        const createdId = payload.tourId ?? payload.TourId ?? null;
        setCurrentTourId(createdId);
        setStatusNote(t('tournaments.createdWithId', { id: createdId }));
        fetchTours();
        const debugSuffix = debug ? '&debug=true' : '';
        if (createdId) {
          router.push(`/tour/${createdId}/round-init${debugSuffix}`);
        }
      }
    });
    const unsubscribeError = socket.onError(() => null);
    const unsubscribeConnection = socket.onConnectionChange((state) => {
      if (state !== 'connected') return;
      if (!pendingTourPayloadRef.current) return;
      const payload = pendingTourPayloadRef.current;
      pendingTourPayloadRef.current = null;
      const sent = socket.send(payload);
      if (sent) {
        setStatusNote(t('tournaments.creating'));
      } else {
        pendingTourPayloadRef.current = payload;
      }
    });
    return () => {
      unsubscribe();
      unsubscribeError();
      unsubscribeConnection();
    };
  }, [debug, fetchTours, isFocused, router, user?.token, t]);

  useEffect(() => {
    if (!tourStarting || !pendingRequestIdRef.current) return;
    const timer = setTimeout(() => {
      if (!pendingRequestIdRef.current) return;
      pendingRequestIdRef.current = null;
      setTourStarting(false);
      setStatusNote(t('tournaments.createFailed'));
    }, 12000);
    return () => clearTimeout(timer);
  }, [tourStarting, t]);

  useEffect(() => {
    if (!isNavigationReady) return;
    if (!user?.token) {
      clear();
      router.replace('/login');
      return;
    }
    fetchTours();
  }, [fetchTours, isNavigationReady, user?.token, clear, router]);

  const handleNewTour = () => {
    if (tourStarting) return;
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const requestId = generateId();
    pendingRequestIdRef.current = requestId;
    setTourStarting(true);
    setError(null);
    setStatusNote(t('tournaments.creating'));
    const payload = {
      type: 'tour.initiate',
      requestId,
      tourName: newTourName.trim() || t('tournaments.defaultName'),
    };
    const sent = socket.send(payload);
    if (!sent) {
      pendingTourPayloadRef.current = payload;
      setStatusNote(t('tournaments.statusUpdateQueued'));
    }
  };

  const updateTourStatus = (tourId: string, status: 'paused' | 'archived' | 'cancelled') => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const requestId = generateId();
    pendingStatusRequestIdRef.current = requestId;
    const sent = socket.send({
      type: 'tour.update',
      tourId,
      requestId,
      status,
    });
    if (!sent) {
      setStatusNote(t('tournaments.statusUpdateQueued'));
    } else {
      setStatusNote(t('tournaments.statusUpdateSent', { status }));
    }
    logToBff('tournaments.status.selected', { tourId, status, requestId });
    if (status === 'archived') {
      console.info('[tournaments] archive', { tourId, status });
    }
    setOpenTourMenuId(null);
    setTours((prev) => {
      if (status === 'archived' || status === 'cancelled') {
        return prev.filter((t) => t.id !== tourId);
      }
      return prev.map((t) => (t.id === tourId ? { ...t, status } : t));
    });
  };

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/login" />;

  return (
    <>
      <ScrollView
        contentContainerStyle={styles.container}
        onScrollBeginDrag={() => setOpenTourMenuId(null)}
      >
        {debug && user ? (
          <Text style={styles.debugUser}>User: {user.forename ?? user.id} ({user.id})</Text>
        ) : null}
        <Image source={TrophyImage} style={styles.heroImage} resizeMode="contain" />
        <Text style={styles.title}>{t('tournaments.title')}</Text>
          <Text style={styles.meta}>{statusNote}</Text>
          {loading ? <Text style={styles.meta}>{t('tournaments.loading')}</Text> : null}
          {error ? <Text style={styles.error}>{error}</Text> : null}
        <Pressable
          style={[styles.toggleButton, showArchived && styles.toggleButtonActive]}
          onPress={() => setShowArchived((prev) => !prev)}
          accessibilityRole="switch"
          accessibilityState={{ checked: showArchived }}
        >
          <View style={styles.toggleIndicator}>
            <View style={[styles.toggleDot, showArchived && styles.toggleDotActive]} />
          </View>
          <Text style={styles.toggleButtonText}>
            {showArchived ? t('tournaments.hideArchived') : t('tournaments.showArchived')}
          </Text>
        </Pressable>
        {visibleTours.map((tour) => {
          const normalizedStatus = (tour.status ?? '').toLowerCase();
          const statusLabel = normalizedStatus === 'active' ? t('tournaments.statusContinue') : tour.status;
          const tourTitle = tour.tourName ?? t('tournaments.defaultName');
          const playerLine = (tour.playerNames ?? []).map((name) => name.trim()).filter(Boolean).join(', ');
          const menuOpen = openTourMenuId === tour.id;
          const rawImage = tour.imageThumbKey ?? tour.imageKey ?? '';
          const imageUrl = rawImage && rawImage.startsWith('http') ? rawImage : rawImage ? `${apiBaseUrl}/api/tours/${tour.id}/image` : '';
          const imageSource = imageUrl ? { uri: imageUrl } : TrophyImage;
          return (
            <View key={tour.id} style={[styles.card, menuOpen && styles.cardMenuOpen]}>
              <Pressable
                style={styles.tourMenuButton}
                onPress={() => setOpenTourMenuId(menuOpen ? null : tour.id)}
                accessibilityLabel={t('tournaments.menuLabel')}
              >
                <Text style={styles.tourMenuIcon}>⋯</Text>
              </Pressable>
              <Pressable
                style={styles.cardContent}
                onPress={() => {
                  setCurrentTourId(tour.id);
                  const debugSuffix = debug ? '?debug=true' : '';
                  router.push(`/tour/${tour.id}/round-init${debugSuffix}`);
                }}
              >
                <View style={styles.cardRow}>
                  <Image source={imageSource} style={styles.tourImage} resizeMode="cover" />
                  <View style={styles.cardText}>
                    <View style={styles.cardTitleRow}>
                      <Text style={styles.cardTitle}>{tourTitle}</Text>
                      {statusLabel ? <Text style={styles.cardStatus}>{statusLabel}</Text> : null}
                    </View>
                    {playerLine ? <Text style={styles.meta}>{playerLine}</Text> : null}
                    {debug ? <Text style={styles.meta}>ID: {tour.id}</Text> : null}
                    <Text style={styles.link}>{t('tournaments.openCta')}</Text>
                  </View>
                </View>
              </Pressable>
              {menuOpen ? (
                <View style={styles.tourMenu}>
                  <Pressable style={styles.tourMenuItem} onPress={() => updateTourStatus(tour.id, 'archived')}>
                    <Text style={styles.tourMenuText}>{t('tournaments.statusArchive')}</Text>
                  </Pressable>
                  <Pressable
                    style={styles.tourMenuItem}
                    onPress={() => {
                      console.info('[tournaments] sleep selected', { tourId: tour.id });
                      logToBff('tournaments.status.selected', {
                        tourId: tour.id,
                        status: 'sleep',
                        requestId: generateId(),
                      });
                      setOpenTourMenuId(null);
                    }}
                  >
                    <Text style={styles.tourMenuText}>{t('tournaments.statusSleep')}</Text>
                  </Pressable>
                  <Pressable style={styles.tourMenuItem} onPress={() => updateTourStatus(tour.id, 'cancelled')}>
                    <Text style={styles.tourMenuText}>{t('tournaments.statusCancel')}</Text>
                  </Pressable>
                </View>
              ) : null}
            </View>
          );
        })}
        {!visibleTours.length && !loading && !error ? <Text style={styles.subtitle}>{t('tournaments.noTours')}</Text> : null}
        {currentTourId ? <Text style={styles.smallNote}>{t('tournaments.activeTour', { id: currentTourId })}</Text> : null}
        <TextInput
          value={newTourName}
          onChangeText={setNewTourName}
          placeholder={t('tournaments.namePlaceholder')}
          placeholderTextColor="#9fb3ff"
          style={styles.tourInput}
        />
        <Pressable
          style={[styles.primaryButton, tourStarting && styles.primaryButtonDisabled]}
          onPress={handleNewTour}
          disabled={tourStarting}
        >
          <Text style={styles.primaryText}>{tourStarting ? t('tournaments.creating') : t('tournaments.createCta')}</Text>
        </Pressable>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 20,
    gap: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0c1328',
  },
  heroImage: {
    width: 140,
    height: 140,
  },
  title: {
    fontSize: 26,
    fontWeight: '800',
    color: '#f8fafc',
  },
  subtitle: {
    color: '#d5e1ff',
    textAlign: 'center',
  },
  smallNote: {
    color: '#d1ffe3',
  },
  card: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: '#0f1c3c',
    gap: 8,
    flexDirection: 'row',
    alignItems: 'flex-start',
    position: 'relative',
    zIndex: 1,
  },
  cardMenuOpen: {
    borderColor: '#6da1ff',
    zIndex: 10,
    elevation: 10,
  },
  cardContent: {
    flex: 1,
  },
  cardRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  cardText: {
    flex: 1,
    gap: 6,
  },
  tourImage: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: '#0b1224',
  },
  tourImagePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    backgroundColor: '#0b1224',
  },
  tourMenuButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#162247',
    borderWidth: 1,
    borderColor: 'rgba(130,168,255,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  tourMenuIcon: {
    color: '#d6e0ff',
    fontSize: 20,
    marginTop: -4,
  },
  tourMenu: {
    position: 'absolute',
    top: 48,
    left: 12,
    backgroundColor: '#0f1834',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(125,157,255,0.35)',
    paddingVertical: 8,
    minWidth: 140,
    zIndex: 20,
    elevation: 20,
  },
  tourMenuItem: {
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  tourMenuText: {
    color: '#e6eeff',
    fontSize: 13,
    fontWeight: '700',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#e5edff',
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  cardStatus: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: '#1f2d4f',
    borderWidth: 1,
    borderColor: '#2c3a6a',
    color: '#cfe0ff',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'capitalize',
  },
  meta: {
    color: '#9fb3ff',
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#33457a',
    backgroundColor: '#111b3a',
  },
  toggleButtonActive: {
    borderColor: '#4f7cff',
    backgroundColor: '#162657',
  },
  toggleButtonText: {
    color: '#dbeafe',
    fontSize: 13,
    fontWeight: '700',
  },
  toggleIndicator: {
    width: 34,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#0b1431',
    borderWidth: 1,
    borderColor: '#33457a',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  toggleDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#64748b',
    alignSelf: 'flex-start',
  },
  toggleDotActive: {
    backgroundColor: '#34d399',
    alignSelf: 'flex-end',
  },
  link: {
    color: '#34d399',
    fontWeight: '700',
  },
  primaryButton: {
    marginTop: 8,
    backgroundColor: '#34d399',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1aa374',
    shadowColor: '#34d399',
    shadowOpacity: 0.6,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
  },
  primaryText: {
    color: '#0c1328',
    fontWeight: '800',
    fontSize: 16,
    textAlign: 'center',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  tourInput: {
    width: '100%',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    backgroundColor: '#0c1328',
    color: '#e5edff',
    marginTop: 12,
  },
  error: {
    color: '#ff7b7b',
    marginTop: 4,
  },
  debugUser: {
    alignSelf: 'flex-end',
    color: '#94a3b8',
    fontSize: 12,
  },
});

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  // RFC4122-ish v4 fallback for environments without crypto.randomUUID
  const random = (size: number) =>
    Array.from({ length: size }, () => Math.floor(Math.random() * 256));
  const bytes = typeof crypto !== 'undefined' && crypto.getRandomValues ? crypto.getRandomValues(new Uint8Array(16)) : Uint8Array.from(random(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
}
